/* eslint import/extensions: 0, import/no-extraneous-dependencies: 0, import/no-unresolved: 0 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import {
  createScope, createTimeline, onScroll,
} from 'animejs';
import 'animejs/adapters/three';

const MODEL_URL = 'models/skeleton-watch.glb';
const ENVIRONMENT_URL = 'environments/watch-studio-1k.hdr';
const AUDIO_URLS = {
  tick: 'audio/watch-tick.ogg',
  assembly: 'audio/watch-assembly.ogg',
};
const REQUIRED_PARTS = [
  'case', 'movement', 'gears', 'dial', 'hands', 'crystal', 'crown',
];
const SAPPHIRE_SETTINGS = {
  color: 0xe9fbff,
  metalness: 0,
  roughness: 0.03,
  transmission: 0.98,
  thickness: 0.18,
  ior: 1.76,
  dispersion: 0.006,
  attenuationDistance: Infinity,
  opacity: 1,
  transparent: false,
  depthWrite: true,
  envMapIntensity: 1.35,
};
const ASSEMBLY_CLICK_THRESHOLDS = [0.28, 0.52, 0.72, 0.9];

const getAssemblyClicks = (previous, current, fired) => (
  current <= previous
    ? []
    : ASSEMBLY_CLICK_THRESHOLDS
      .map((threshold, index) => (
        !fired[index] && previous < threshold && current >= threshold ? index : -1
      ))
      .filter((index) => index >= 0)
);

const getInitialMuted = () => {
  try {
    return window.localStorage.getItem('watchAudioMuted') === 'true';
  } catch (error) {
    return false;
  }
};

function SkeletonWatchScene({ reducedEffects }) {
  const containerRef = useRef(null);
  const audioControllerRef = useRef(null);
  const [status, setStatus] = useState(reducedEffects ? 'fallback' : 'loading');
  const [muted, setMuted] = useState(getInitialMuted);

  useEffect(() => {
    audioControllerRef.current?.setMuted(muted);
  }, [muted]);

  useEffect(() => {
    if (reducedEffects) return undefined;

    const container = containerRef.current;
    const scrollRoot = container?.closest('.cinematic-scroll');
    const introRoot = scrollRoot?.querySelector('.scroll-chapters');
    const portfolioRoot = scrollRoot?.querySelector('.portfolio-sections');
    if (!container || !scrollRoot || !introRoot || !portfolioRoot) return undefined;

    let renderer;
    let resizeObserver;
    let sceneObserver;
    let animationFrame;
    let animeScope;
    let scene;
    let environmentTarget;
    let hdrTexture;
    let audioListener;
    let tickSound;
    let clickSounds = [];
    let mounted = true;
    let active = true;
    let audioReady = false;
    let audioActivated = false;
    let audioMuted = muted;

    try {
      const mobile = window.matchMedia('(max-width: 768px)').matches;
      const lowPower = mobile
        || (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4);

      renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: !lowPower,
        powerPreference: 'high-performance',
      });
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, lowPower ? 1.25 : 1.8));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.15;
      renderer.transmissionResolutionScale = lowPower ? 0.5 : 1;
      renderer.shadowMap.enabled = !lowPower;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      container.appendChild(renderer.domElement);

      scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
      camera.position.set(0, 0, mobile ? 15.5 : 13);
      scene.add(camera);

      const watchRig = new THREE.Group();
      scene.add(watchRig);

      scene.add(new THREE.HemisphereLight(0xdff8ff, 0x020617, 0.65));
      const keyLight = new THREE.DirectionalLight(0xffffff, 2.8);
      keyLight.position.set(5, 5, 8);
      keyLight.castShadow = !lowPower;
      keyLight.shadow.mapSize.set(1024, 1024);
      scene.add(keyLight);
      const cyanRim = new THREE.PointLight(0x22d3ee, 18, 20, 2);
      cyanRim.position.set(-5, 1, 5);
      scene.add(cyanRim);
      const warmRim = new THREE.PointLight(0xf5b94c, 9, 14, 2);
      warmRim.position.set(4, -3, 4);
      scene.add(warmRim);

      const pmremGenerator = new THREE.PMREMGenerator(renderer);
      pmremGenerator.compileEquirectangularShader();
      new RGBELoader().load(
        ENVIRONMENT_URL,
        (texture) => {
          if (!mounted) {
            texture.dispose();
            return;
          }
          hdrTexture = texture;
          environmentTarget = pmremGenerator.fromEquirectangular(texture);
          scene.environment = environmentTarget.texture;
          texture.dispose();
          hdrTexture = undefined;
          pmremGenerator.dispose();
        },
        undefined,
        () => pmremGenerator.dispose(),
      );

      audioListener = new THREE.AudioListener();
      camera.add(audioListener);

      const syncTickSound = async () => {
        if (!audioReady || !audioActivated || !tickSound) return;
        const shouldPlay = active && !document.hidden && !audioMuted;
        if (shouldPlay && !tickSound.isPlaying) {
          if (audioListener.context.state === 'suspended') {
            await audioListener.context.resume();
          }
          if (mounted && !tickSound.isPlaying) tickSound.play();
        } else if (!shouldPlay && tickSound.isPlaying) {
          tickSound.pause();
        }
      };

      const activateAudio = () => {
        audioActivated = true;
        syncTickSound();
      };

      audioControllerRef.current = {
        activate: activateAudio,
        setMuted: (nextMuted) => {
          audioMuted = nextMuted;
          audioListener.setMasterVolume(nextMuted ? 0 : 1);
          syncTickSound();
        },
      };

      const onFirstInteraction = (event) => {
        if (event.target?.closest?.('.watch-sound-toggle')) return;
        activateAudio();
        window.removeEventListener('pointerdown', onFirstInteraction);
        window.removeEventListener('keydown', onFirstInteraction);
      };
      window.addEventListener('pointerdown', onFirstInteraction);
      window.addEventListener('keydown', onFirstInteraction);

      const onVisibilityChange = () => syncTickSound();
      document.addEventListener('visibilitychange', onVisibilityChange);

      const audioLoader = new THREE.AudioLoader();
      Promise.all([
        audioLoader.loadAsync(AUDIO_URLS.tick),
        audioLoader.loadAsync(AUDIO_URLS.assembly),
      ]).then(([tickBuffer, clickBuffer]) => {
        if (!mounted) return;
        tickSound = new THREE.PositionalAudio(audioListener);
        tickSound.setBuffer(tickBuffer);
        tickSound.setLoop(true);
        tickSound.setVolume(0.12);
        tickSound.setRefDistance(8);
        tickSound.setRolloffFactor(1.4);
        watchRig.add(tickSound);

        clickSounds = Array.from({ length: 4 }, () => {
          const sound = new THREE.PositionalAudio(audioListener);
          sound.setBuffer(clickBuffer);
          sound.setVolume(0.24);
          sound.setRefDistance(8);
          sound.setRolloffFactor(1.2);
          watchRig.add(sound);
          return sound;
        });
        audioReady = true;
        syncTickSound();
      }).catch(() => {});

      const resize = () => {
        const { width, height } = container.getBoundingClientRect();
        if (!width || !height) return;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height, false);
      };
      resize();
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(container);
      } else {
        window.addEventListener('resize', resize);
      }

      sceneObserver = new IntersectionObserver(
        ([entry]) => {
          active = entry.isIntersecting;
          syncTickSound();
        },
        { threshold: 0.01 },
      );
      sceneObserver.observe(scrollRoot);

      const loader = new GLTFLoader();
      loader.load(
        MODEL_URL,
        ({ scene: loadedModel }) => {
          if (!mounted) return;
          const model = loadedModel;

          const parts = Object.fromEntries(
            REQUIRED_PARTS.map((name) => [name, model.getObjectByName(name)]),
          );
          const missing = REQUIRED_PARTS.filter((name) => !parts[name]);
          if (missing.length) {
            setStatus('failed');
            return;
          }

          model.scale.setScalar(mobile ? 0.78 : 0.9);
          model.rotation.set(-0.08, 0.12, -0.04);
          const sapphireMaterial = new THREE.MeshPhysicalMaterial(SAPPHIRE_SETTINGS);
          const crystalMeshes = [];
          parts.crystal.traverse((object) => {
            if (object.isMesh) crystalMeshes.push(object);
          });
          for (let index = 0; index < crystalMeshes.length; index += 1) {
            const crystalMesh = crystalMeshes[index];
            crystalMesh.material = sapphireMaterial;
            crystalMesh.castShadow = false;
            crystalMesh.receiveShadow = false;
            crystalMesh.renderOrder = 10;
          }
          const opaqueMeshes = [];
          model.traverse((object) => {
            if (object.isMesh && !parts.crystal.getObjectById(object.id)) {
              opaqueMeshes.push(object);
            }
          });
          for (let index = 0; index < opaqueMeshes.length; index += 1) {
            opaqueMeshes[index].castShadow = !lowPower;
            opaqueMeshes[index].receiveShadow = !lowPower;
          }
          watchRig.add(model);

          const gears = parts.gears.children.filter((child) => child.name.startsWith('gear_'));
          const balance = model.getObjectByName('balance_wheel');
          const escapement = model.getObjectByName('escapement');
          const hourHand = model.getObjectByName('hour_hand');
          const minuteHand = model.getObjectByName('minute_hand');
          const secondHand = model.getObjectByName('second_hand');
          const handRotations = [hourHand, minuteHand, secondHand]
            .map((hand) => hand?.rotation.z || 0);

          const finalZ = Object.fromEntries(
            REQUIRED_PARTS.map((name) => [name, parts[name].position.z]),
          );
          const gearFinalZ = gears.map((gear) => gear.position.z);
          const firedClicks = ASSEMBLY_CLICK_THRESHOLDS.map(() => false);
          const getIntroProgress = () => {
            const rect = introRoot.getBoundingClientRect();
            const distance = Math.max(1, introRoot.offsetHeight - window.innerHeight);
            return THREE.MathUtils.clamp(-rect.top / distance, 0, 1);
          };
          let previousProgress = getIntroProgress();

          animeScope = createScope({ root: container }).add(() => {
            const timeline = createTimeline({
              autoplay: onScroll({
                target: introRoot,
                enter: 'top top',
                leave: 'bottom bottom',
                sync: 0.16,
              }),
              defaults: { ease: 'inOut(3)' },
            });

            timeline
              .add(watchRig, {
                x: [2.5, -1.8, 1.8, 0],
                y: [0.2, -0.25, 0.2, 0],
                scale: [0.72, 0.9, 0.82, 0.92],
                rotateX: [-10, 8, -5, 0],
                rotateY: [-24, 18, -12, 0],
                duration: 4800,
              }, 0)
              .add(parts.movement, {
                z: [-5.5, -1.6, finalZ.movement],
                scale: [0.72, 0.9, 1],
                duration: 1800,
              }, 0)
              .add(parts.case, {
                z: [-8, -3, finalZ.case],
                scale: [0.82, 1],
                duration: 1350,
              }, 2380)
              .add(parts.crown, {
                x: [8, 3.82, 0],
                z: [4, finalZ.crown],
                duration: 900,
              }, 2780)
              .add(parts.dial, {
                z: [8, 3, finalZ.dial],
                scale: [0.75, 1],
                duration: 1100,
              }, 2560)
              .add(parts.hands, {
                z: [10, 4, finalZ.hands],
                scale: [0, 1],
                rotateZ: [-120, 0],
                duration: 980,
              }, 3140)
              .add(parts.crystal, {
                z: [13, 6, finalZ.crystal],
                scale: [0.8, 1],
                duration: 1050,
              }, 3500)
              .add(cyanRim, {
                x: [-6, 4, -4],
                intensity: [20, 55, 34],
                duration: 4800,
              }, 0)
              .add(camera, {
                fov: [39, 34, 37, 34],
                duration: 4800,
              }, 0);

            gears.forEach((gear, index) => {
              timeline.add(gear, {
                z: [6 + index * 0.8, 2.5 + index * 0.25, gearFinalZ[index]],
                scale: [0.2, 1],
                duration: 1450,
              }, 520 + index * 150);
            });

            createTimeline({
              autoplay: onScroll({
                target: portfolioRoot,
                enter: 'top bottom',
                leave: 'bottom bottom',
                sync: 0.16,
              }),
              defaults: { ease: 'inOut(3)' },
            }).add(watchRig, {
              x: [0, 3.1, -3, 3.1, -3, 2.8],
              y: [0, 0.15, -0.2, 0.16, -0.14, 0],
              scale: [0.92, 0.58, 0.54, 0.56, 0.52, 0.54],
              rotateY: [0, 18, -16, 20, -18, 16],
              duration: 5000,
            }, 0);
          });

          const clock = new THREE.Clock();
          const render = () => {
            if (!mounted) return;
            animationFrame = window.requestAnimationFrame(render);
            if (!active || document.hidden) return;

            const elapsed = clock.getElapsedTime();
            const introProgress = getIntroProgress();
            getAssemblyClicks(previousProgress, introProgress, firedClicks)
              .forEach((index) => {
                firedClicks[index] = true;
                if (audioReady && audioActivated && !audioMuted) clickSounds[index]?.play();
              });
            previousProgress = introProgress;
            model.rotation.y = 0.12 + Math.sin(elapsed * 0.35) * 0.035;
            for (let index = 0; index < gears.length; index += 1) {
              gears[index].rotation.z = elapsed * (index % 2 ? -0.3 : 0.25);
            }
            if (balance) balance.rotation.z = Math.sin(elapsed * 4.5) * 0.38;
            if (escapement) escapement.rotation.z = Math.sin(elapsed * 4.5) * 0.12;
            if (hourHand) hourHand.rotation.z = handRotations[0] - elapsed * 0.00012;
            if (minuteHand) minuteHand.rotation.z = handRotations[1] - elapsed * 0.0014;
            if (secondHand) secondHand.rotation.z = handRotations[2] - elapsed * 0.085;
            renderer.render(scene, camera);
          };
          render();
          setStatus('ready');
        },
        undefined,
        () => {
          if (mounted) setStatus('failed');
        },
      );

      return () => {
        mounted = false;
        window.cancelAnimationFrame(animationFrame);
        animeScope?.revert();
        audioControllerRef.current = null;
        resizeObserver?.disconnect();
        sceneObserver?.disconnect();
        tickSound?.stop();
        clickSounds.forEach((sound) => sound.stop());
        camera.remove(audioListener);
        window.removeEventListener('pointerdown', onFirstInteraction);
        window.removeEventListener('keydown', onFirstInteraction);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('resize', resize);

        const geometries = new Set();
        const materials = new Set();
        scene.traverse((object) => {
          if (object.geometry) geometries.add(object.geometry);
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => materials.add(material));
          } else if (object.material) {
            materials.add(object.material);
          }
        });
        geometries.forEach((geometry) => geometry.dispose());
        materials.forEach((material) => material.dispose());
        hdrTexture?.dispose();
        environmentTarget?.dispose();
        pmremGenerator.dispose();
        renderer.dispose();
        renderer.forceContextLoss();
        renderer.domElement.remove();
      };
    } catch (error) {
      renderer?.dispose();
      renderer?.domElement?.remove();
      if (mounted) setStatus('failed');
      return () => {
        mounted = false;
      };
    }
  }, [reducedEffects]);

  const showFallback = status !== 'ready';
  const toggleMuted = () => {
    const nextMuted = !muted;
    setMuted(nextMuted);
    try {
      window.localStorage.setItem('watchAudioMuted', String(nextMuted));
    } catch (error) {
      // Storage is optional; sound control still works for this visit.
    }
    if (!nextMuted) audioControllerRef.current?.activate();
  };

  return (
    <div
      ref={containerRef}
      className={[
        'skeleton-watch-scene',
        showFallback ? 'skeleton-watch-scene--fallback' : '',
      ].filter(Boolean).join(' ')}
      data-testid="skeleton-watch-scene"
    >
      {showFallback && (
        <img
          className="skeleton-watch-scene__fallback"
          src="images/skeleton-watch-poster.png"
          alt=""
        />
      )}
      {!reducedEffects && (
        <button
          type="button"
          className="watch-sound-toggle"
          aria-pressed={!muted}
          aria-label={muted ? 'Enable watch sound' : 'Mute watch sound'}
          onClick={toggleMuted}
        >
          <span aria-hidden="true">{muted ? '×' : '♪'}</span>
          {muted ? 'Sound off' : 'Sound on'}
        </button>
      )}
    </div>
  );
}

SkeletonWatchScene.propTypes = {
  reducedEffects: PropTypes.bool,
};

SkeletonWatchScene.defaultProps = {
  reducedEffects: false,
};

export {
  ASSEMBLY_CLICK_THRESHOLDS,
  AUDIO_URLS,
  ENVIRONMENT_URL,
  getAssemblyClicks,
  MODEL_URL,
  REQUIRED_PARTS,
  SAPPHIRE_SETTINGS,
};
export default SkeletonWatchScene;
